import React from 'react';
import { getResourceColor } from '../../../../utils/resourceUtils';
import ToggleSwitch from '../../../shared/ToggleSwitch/ToggleSwitch';
import SegmentedControl from '../../../shared/SegmentedControl/SegmentedControl';
import InfoBox from '../../../shared/InfoBox/InfoBox';
import HelpTooltip from '../../../shared/HelpTooltip/HelpTooltip';
import { getToolLabel } from '../../../shared/toolLabels';

/**
 * @component ProjectPermissionEditor
 * @description Right column editor for project role and permissions.
 */
const ProjectPermissionEditor = ({
    editingUser,
    userForm,
    setUserForm,
    setEditingUser,
    handleSubmitPermissions,
    handleRemoveUser,
    setPermission,
    setJournalValue,
    getInitials,
    readOnly,
    activeTab // New prop
}) => {
    const editingUserRole = editingUser?.project_role || editingUser?.role || '';
    // Safety destructuring of permissions
    const perms = userForm.permissions || {};
    const journalMode = perms.journal?.mode || 'draft';

    // Compute segment value for Bautagesbericht
    const getJournalSegmentValue = () => {
        if (journalMode === 'draft') return 'draft';
        if (perms.journal?.full_level === 'manage') return 'manage';
        return 'read';
    };

    // Handle segment change
    const handleJournalSegmentChange = (val) => {
        if (val === 'draft') {
            setJournalValue('mode', 'draft');
            setJournalValue('full_level', 'read');
            setJournalValue('draft_visibility', 'own');
            setJournalValue('draft_action', 'own');
        } else if (val === 'manage') {
            setJournalValue('mode', 'full');
            setJournalValue('full_level', 'manage');
        } else {
            // 'read'
            setJournalValue('mode', 'full');
            setJournalValue('full_level', 'read');
        }
    };

    // Compute project admin toggle state
    const isProjectAdmin = !!(perms.team?.manage && perms.settings?.edit_project_data);

    const handleProjectAdminChange = (checked) => {
        setPermission('team', 'manage', checked);
        setPermission('settings', 'edit_project_data', checked);
    };

    // Dynamische Beschreibungstexte für die Stufen des Bautagesberichts
    const getJournalSegmentDescription = () => {
        const current = getJournalSegmentValue();
        if (current === 'draft') {
            return 'Reicht eigene Einträge als Entwurf ein und bearbeitet nur die eigenen Entwürfe.';
        }
        if (current === 'read') {
            return 'Darf das vollständige Bautagebuch einsehen. Kann keine Einträge bearbeiten oder freigeben.';
        }
        return 'Vollzugriff auf das Bautagebuch. Kann Berichte erstellen, bearbeiten und Zuarbeiter-Entwürfe freigeben.';
    };

    return (
        <div className={`team-permissions-editor ${readOnly ? 'read-only' : ''}`}>
            <div className="editor-header">
                <div className="editor-header-content">
                    {editingUser ? (
                        <>
                            <div className="avatar-initials" style={{ width: '32px', height: '32px', fontSize: '12px', backgroundColor: editingUserRole === 'Fahrzeug' ? '#f3f4f6' : getResourceColor(editingUser) }}>
                                {editingUserRole === 'Fahrzeug' ? '🚛' : getInitials(editingUser.name)}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div className="editor-title">{editingUser.plate || editingUser.name}</div>
                                <div className="editor-subtitle">{editingUserRole === 'Fahrzeug' ? (editingUser.global_role || 'Fahrzeug') : (editingUser.employee_role || editingUser.role || 'Mitglied')}</div>
                            </div>
                            <button type="button" className="co-modal-close-btn" onClick={() => setEditingUser(null)}>✕</button>
                        </>
                    ) : (
                        <div className="editor-header-empty-title">Details & Berechtigungen</div>
                    )}
                </div>
            </div>

            {editingUser ? (
                editingUserRole === 'blacklisted' ? (
                    <div className="blacklist-editor-state">
                        <div className="blacklist-content">
                            <span className="blacklist-icon">👤</span>
                            <h3>Blacklist-Eintrag</h3>
                            <p>
                                Dieser Nutzer hat aktuell keinen Zugriff auf das Projekt.
                                Berechtigungen können erst wieder verwaltet werden, wenn die Person
                                von der Blacklist entfernt und regulär zum Team hinzugefügt wird.
                            </p>
                            <button
                                type="button"
                                className="btn-remove-from-blacklist"
                                onClick={() => handleRemoveUser(editingUser)}
                            >
                                Von Blacklist entfernen
                            </button>
                        </div>
                    </div>
                ) : editingUserRole === 'Fahrzeug' ? (
                    <div className="blacklist-editor-state">
                        <div className="blacklist-content">
                            <span className="blacklist-icon" style={{ background: '#f1f5f9', color: '#64748b', boxShadow: 'none' }}>🚛</span>
                            <h3>Fahrzeug-Verwaltung</h3>
                            <p>
                                Fahrzeuge werden als rein operative Ressourcen geführt.
                                Individuelle App-Berechtigungen können nur für Personen mit Benutzerkonto vergeben werden.
                            </p>
                            <div className="form-actions" style={{ marginTop: '24px', width: '100%' }}>
                                <button type="button" className="delete-btn" style={{ width: '100%', flex: 'none' }} onClick={() => handleRemoveUser(editingUser)}>Entfernen</button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmitPermissions} className="permission-form">
                        <div className="ios-form-row">
                            <span className="ios-form-row-label">Funktion im Projekt</span>
                                <input
                                    type="text"
                                    placeholder={editingUserRole === 'Fahrzeug' ? 'z.B. LKW, PKW...' : 'z.B. Monteur, Bauleiter...'}
                                    value={userForm.role}
                                    onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                                    className="ios-form-row-input"
                                />
                        </div>

                        {editingUser.user_id ? (
                            <div className="permissions-scroll-container">

                                {/* 1. Karte: Projekt-Verwaltung */}
                                <div className="co-card">
                                    <div className="co-card-header" style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div className="co-card-title">
                                            <span>🛠️</span>
                                            <span>Projektleitung / Admin</span>
                                        </div>
                                        <HelpTooltip
                                            text="Erlaubt das Verwalten von Team-Mitgliedern und Ändern aller Projekt-Stammdaten."
                                            position="left"
                                        />
                                    </div>
                                    <ToggleSwitch
                                        checked={isProjectAdmin}
                                        onChange={handleProjectAdminChange}
                                        label="Vollzugriff vergeben"
                                    />
                                </div>

                                {/* 2. Karte: Journal */}
                                <div className="co-card">
                                    <div className="co-card-header" style={{ marginBottom: '14px' }}>
                                        <div className="co-card-title">
                                            <span>📝</span>
                                            <span>{getToolLabel('journal')}</span>
                                        </div>
                                    </div>
                                    <SegmentedControl
                                        options={[
                                            { value: 'draft', label: 'Zuarbeiter' },
                                            { value: 'read', label: 'Leser' },
                                            { value: 'manage', label: 'Manager' }
                                        ]}
                                        value={getJournalSegmentValue()}
                                        onChange={handleJournalSegmentChange}
                                    />
                                    <div className="co-card-body" style={{ marginTop: '10px', fontSize: '11.5px', color: '#86868b' }}>
                                        {getJournalSegmentDescription()}
                                    </div>

                                </div>

                                {/* 3. Teaser-Karte: Zeiterfassung */}
                                <div className="co-card disabled">
                                    <div className="co-card-header">
                                        <div className="co-card-title">
                                            <span>⏱️</span>
                                            <span>{getToolLabel('timesheet')}</span>
                                        </div>
                                        <span className="co-card-badge">In Kürze</span>
                                    </div>
                                    <div className="co-card-body">
                                        Zeiterfassungs-Rechte und Freigabestufen für dieses Projekt werden demnächst hier konfigurierbar sein.
                                    </div>
                                </div>

                                {/* 4. Teaser-Karte: Material */}
                                <div className="co-card disabled">
                                    <div className="co-card-header">
                                        <div className="co-card-title">
                                            <span>📦</span>
                                            <span>{getToolLabel('internalHandover')}</span>
                                        </div>
                                        <span className="co-card-badge">In Kürze</span>
                                    </div>
                                    <div className="co-card-body">
                                        Berechtigungen für Lieferscheine, Materialbestellungen und Geräteanforderungen.
                                    </div>
                                </div>

                            </div>
                        ) : (
                            <div style={{ marginTop: '10px', marginBottom: '20px' }}>
                                <InfoBox variant="info" icon="ℹ️" title="Kein Benutzerkonto aktiv">
                                    Dieser Mitarbeiter wird rein operativ geführt. Um Berechtigungen zu vergeben, erstellen Sie bitte im Admin-Panel ein Benutzerkonto für diesen Mitarbeiter.
                                </InfoBox>
                            </div>
                        )}

                        <div className="form-actions">
                            <button type="submit" className="save-btn">Rechte speichern</button>
                            <button type="button" className="delete-btn" onClick={() => handleRemoveUser()}>Entfernen</button>
                        </div>
                    </form>
                )
            ) : (
                activeTab === 'fahrzeuge' ? (
                    <div className="blacklist-editor-state">
                        <div className="blacklist-content">
                            <span className="blacklist-icon" style={{ background: '#f1f5f9', color: '#64748b', boxShadow: 'none' }}>🚛</span>
                            <h3>Fahrzeug-Verwaltung</h3>
                            <p>
                                Fahrzeuge werden als rein operative Ressourcen geführt.
                                Individuelle App-Berechtigungen können nur für Personen mit Benutzerkonto vergeben werden.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="editor-empty-state">
                        <div className="empty-icon">👤</div>
                        <p className="empty-text-main">Wähle ein Mitglied aus der Liste aus.</p>
                        <p className="empty-text-sub">Oder nutze die Sidebar links zum Hinzufügen.</p>
                    </div>
                )
            )}
        </div>
    );
};

export default ProjectPermissionEditor;
